import { Box, Divider, Grid } from '@mui/material';

import { LabelBadge } from '@/components/Badge';
import {
  InfoReviewFieldLabel,
  InfoReviewFieldValue,
} from '@/components/Form/Form.styled';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import {
  selectSameOperationIdAccountTypes,
  selectUser,
} from '@/redux/user-data/selectors';

import { User, UserAccountType } from '@/types/user';

import { getFullName } from '@/utils/string';
import { isIndividualUser } from '@/utils/user';

import { ReviewCard } from './ReviewCard';
import { SectionProps } from './types';

export function AccountsSection({
  currentIndex,
  totalSections,
}: Readonly<SectionProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectUser);
  const accountTypes = useAppSelector(selectSameOperationIdAccountTypes);

  return (
    <ReviewCard
      title={t('moreInfo.reviewMyInformation.accounts.title')}
      currentIndex={currentIndex}
      totalSections={totalSections}
    >
      {accountTypes.map((account: UserAccountType, index: number) => (
        <Box key={account.id}>
          {index > 0 && <Divider sx={{ mt: 1.5 }} />}
          <AccountBlock account={account} user={user} />
        </Box>
      ))}
    </ReviewCard>
  );
}

type AccountBlockProps = {
  account: UserAccountType;
  user?: User;
};

function AccountBlock({ account, user }: Readonly<AccountBlockProps>) {
  const { t } = useTranslate();

  const fullName = getFullName(user);

  const fieldPrefix = 'moreInfo.reviewMyInformation.accounts.field';

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.accountName`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {account.name} ({account.currency})
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.accountType`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {t(`${fieldPrefix}.accountType.${account.type}`)}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>

      <InfoReviewFieldLabel>
        {t(`${fieldPrefix}.accountHolders`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>{fullName}</InfoReviewFieldValue>

      <InfoReviewFieldLabel>
        {t(`${fieldPrefix}.authorisedUsers`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {fullName}
        {isIndividualUser(user) && (
          <LabelBadge
            label={t(`${fieldPrefix}.signAlone`)}
            sx={{ ml: 1, px: 0.25 }}
          />
        )}
      </InfoReviewFieldValue>

      <InfoReviewFieldLabel>
        {t(`${fieldPrefix}.accountFor`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {t(
          `selectApplicationType.applicationTypeSelection.actionButton.${user?.applicationType}`,
          { defaultValue: '' },
        )}
      </InfoReviewFieldValue>

      <InfoReviewFieldLabel>
        {t(`${fieldPrefix}.businessPurpose`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {account.isBusinessPurpose ? t('common.yes') : t('common.no')}
      </InfoReviewFieldValue>
    </Box>
  );
}
